import React, { useState } from 'react';
import { X, Video } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToastContext } from '../../contexts/ToastContext';
import { bookConsultation } from '../../services/consultationService';
import Button from '../shared/Button';
import Input from '../shared/Input';
import CustomSelect from '../shared/CustomSelect';
import { DatePicker, TimePicker } from './index';

const BookConsultationModal = ({ isOpen, onClose, vendorId, vendorName, serviceId, questions = [] }) => {
  const { user, isAuthenticated } = useAuth();
  const toast = useToastContext();
  const [saving, setSaving] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [responses, setResponses] = useState({});

  const resetForm = () => {
    setScheduledDate('');
    setScheduledTime('');
    setResponses({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const renderQuestion = (q, idx) => {
    const id = q.id || `q_${idx}`;
    const value = responses[id] ?? '';

    if (q.type === 'textarea') {
      return (
        <div key={id} className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            {q.label} {q.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={value}
            onChange={(e) => handleResponseChange(id, e.target.value)}
            placeholder={q.placeholder}
            required={q.required}
            rows={3}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500"
          />
        </div>
      );
    }

    if (q.type === 'select') {
      const selectOptions = (q.options || []).filter(Boolean).map((opt) => ({ value: opt, label: opt }));
      return (
        <CustomSelect
          key={id}
          label={`${q.label}${q.required ? ' *' : ''}`}
          options={selectOptions}
          value={value}
          onChange={(v) => handleResponseChange(id, v)}
          placeholder="Select..."
          required={q.required}
        />
      );
    }

    const inputType = q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : q.type === 'date' ? 'date' : q.type === 'time' ? 'time' : 'text';
    return (
      <div key={id} className="flex flex-col gap-1">
        <label className="text-sm font-medium text-gray-700">
          {q.label} {q.required && <span className="text-red-500">*</span>}
        </label>
        <Input
          type={inputType}
          value={value}
          onChange={(e) => handleResponseChange(id, e.target.value)}
          placeholder={q.placeholder}
          required={q.required}
        />
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error(null, 'Please log in to book a consultation');
      return;
    }
    if (!scheduledDate) {
      toast.error(null, 'Please select a date');
      return;
    }
    const requiredQuestions = questions.filter((q) => q.required);
    const missing = requiredQuestions.filter((q) => {
      const id = q.id || `q_${questions.indexOf(q)}`;
      return !responses[id]?.trim();
    });
    if (missing.length > 0) {
      toast.error(null, 'Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const questionnaireResponses = {};
      questions.forEach((q, idx) => {
        const id = q.id || `q_${idx}`;
        if (responses[id] !== undefined) questionnaireResponses[id] = responses[id];
      });
      await bookConsultation({
        vendor_id: vendorId,
        service_id: serviceId || null,
        scheduled_date: scheduledDate,
        scheduled_time: scheduledTime || null,
        questionnaire_responses: questionnaireResponses,
      });
      toast.success(null, 'Consultation request submitted! The vendor will contact you.');
      handleClose();
    } catch (err) {
      toast.error(null, err?.response?.data?.message || 'Failed to submit consultation');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-[10002] overflow-y-auto animate-fade-in">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-auto animate-scale-in border border-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center">
                <Video className="w-6 h-6 text-secondary-600" />
                </div>
                <div>
                <span className="inline-block px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-1">
                    Book Consultation
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {vendorName ? `Consultation with ${vendorName}` : 'Request a Consultation'}
                  </h3>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 hover:scale-110"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <DatePicker
                    value={scheduledDate}
                    onChange={setScheduledDate}
                    placeholder="Select date"
                    allowPastDates={false}
                    minDate={minDate}
                    required
                    label="Preferred date"
                  />
                </div>
                <div className="flex-1">
                  <TimePicker
                    value={scheduledTime}
                    onChange={setScheduledTime}
                    placeholder="Select time"
                    format="24"
                    label="Preferred time"
                  />
                </div>
              </div>

              {questions.length > 0 && (
                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <p className="text-sm font-medium text-gray-700">Please answer the following:</p>
                  {questions.map((q, idx) => renderQuestion(q, idx))}
                </div>
              )}

              <div className="flex gap-2 pt-4 justify-end">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" variant="secondary" loading={saving} disabled={saving}>
                  Submit Request
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookConsultationModal;
